import { useEffect, useState } from 'react';
import { Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { apiFetch } from '../lib/apiClient';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function PayoutEmailSettings({ truck, readOnly = false, onSaved }) {
  const [email, setEmail] = useState(truck?.payout_email || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEmail(truck?.payout_email || '');
  }, [truck?.truck_id, truck?.payout_email]);

  const trimmed = email.trim();
  const valid = EMAIL_PATTERN.test(trimmed);
  const unchanged = trimmed.toLowerCase() === (truck?.payout_email || '').toLowerCase();

  const handleSave = async () => {
    if (!truck || readOnly || !valid) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/api/trucks/${truck.truck_id}/payout-email`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payout_email: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Failed to save payout email');
      }
      onSaved?.(data);
      toast.success('Payout email saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save payout email');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border p-4 md:p-6 space-y-4" data-testid="payout-email-settings">
      <div>
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <Mail className="w-5 h-5 text-primary" />
          Payouts
        </h3>
        <p className="text-sm text-muted-foreground">
          Daily earnings for this truck are sent to this email by Interac e-Transfer.
        </p>
      </div>

      <div className="space-y-1.5 max-w-sm">
        <Label htmlFor="payout-email" className="text-sm font-medium">Interac e-Transfer email</Label>
        <Input
          id="payout-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={readOnly}
          placeholder="payouts@yourtruck.com"
        />
        {!truck?.payout_email && (
          <p className="text-xs text-destructive">No payout email on file — add one so we can pay you.</p>
        )}
        {trimmed && !valid && (
          <p className="text-xs text-destructive">Enter a valid email address.</p>
        )}
      </div>

      {!readOnly && (
        <Button onClick={handleSave} disabled={saving || !valid || unchanged} data-testid="save-payout-email-btn">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      )}
    </div>
  );
}
